// APIDrawer.jsx
import { useState } from 'react';
import {
    Drawer,
    Box,
    Tabs,
    Tab,
    TextField,
    Select,
    MenuItem,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import { AddCircleOutline, SendOutlined, Delete, Close, ChevronRight } from '@mui/icons-material';
import Styles from './APIDrawer.module.scss'
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { useEditor } from 'views/CodeEditor/contexts';

// KeyValueEditor component handles the tabular input for headers, params, and cookies
const KeyValueEditor = ({ pairs, onChange, onDelete }) => (
    <TableContainer component={Paper}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell className={Styles.actionCell}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {pairs.map((pair, index) => (
                    <TableRow key={index}>
                        <TableCell>
                            <TextField
                                fullWidth
                                size="small"
                                value={pair.key}
                                onChange={(e) => onChange(index, 'key', e.target.value)}
                            />
                        </TableCell>
                        <TableCell>
                            <TextField
                                fullWidth
                                size="small"
                                value={pair.value}
                                onChange={(e) => onChange(index, 'value', e.target.value)}
                            />
                        </TableCell>
                        <TableCell>
                            <IconButton
                                onClick={() => onDelete(index)}
                                size="small"
                            >
                                <Delete />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const APIDrawer = ({ drawerOpen, onClose, resetActivityTimer }) => {
    // State management for request data
    const [method, setMethod] = useState('GET');
    const [endpoint, setEndpoint] = useState('');
    const [requestTab, setRequestTab] = useState(0);
    const [responseTab, setResponseTab] = useState(1);
    const [headers, setHeaders] = useState([{ key: '', value: '' }]);
    const [params, setParams] = useState([{ key: '', value: '' }]);
    const [cookies, setCookies] = useState([{ key: '', value: '' }]);
    const [body, setBody] = useState('');

    const { setUserApiInput } = useEditor();

    // Mock response data
    const [response, setResponse] = useState({
        headers: [{ key: 'Content-Type', value: 'application/json' }],
        body: '{\n  "status": "success"\n}',
        cookies: [{ key: 'session', value: 'example-session-id' }],
        status: '',
        respTime: ''
    });

    // Handlers for key-value pair management
    const handlePairChange = (setter) => (index, field, value) => {
        resetActivityTimer();
        setter(prev => {
            const newPairs = [...prev];
            newPairs[index] = { ...newPairs[index], [field]: value };
            return newPairs;
        });
    };

    const handlePairDelete = (setter) => (index) => {
        resetActivityTimer();
        setter(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddPair = (setter) => () => {
        resetActivityTimer();
        setter(prev => [...prev, { key: '', value: '' }]);
    };

    const isValidEndpoint = () => {
        try {
            // Check if it's a valid URL or path
            if (endpoint.startsWith('/')) {
                return endpoint.length > 1; // Valid path
            }
            new URL(endpoint); // Will throw if invalid URL
            return true;
        } catch {
            return false;
        }
    };

    // Handle endpoint change
    const handleEndpointChange = (e) => {
        resetActivityTimer();
        setEndpoint(e.target.value);
    };

    const parseCookies = (setCookieHeader) => {
        return setCookieHeader.split(',').map((cookieString) => {
            const parts = cookieString.split(';').map((part) => part.trim());
            const [name, value] = parts[0].split('=');
            const attributes = {};
            for (let i = 1; i < parts.length; i++) {
                const [attrName, attrValue] = parts[i].split('=');
                attributes[attrName.toLowerCase()] = attrValue || true; // Handle flags like "HttpOnly"
            }
            return { name, value, attributes };
        });
    };

    const fetchResponseColor = (status) => {
        if (status >= 100 && status < 200) {
            return "blue";
        } else if (status >= 200 && status < 300) {
            return "green";
        } else if (status >= 300 && status < 400) {
            return "yellow";
        } else if (status >= 400 && status < 500) {
            return "purple";
        } else if (status >= 500 && status < 600) {
            return "red";
        } else {
            return "green";
        }
    };

    const handleSendRequest = async () => {
        resetActivityTimer();

        let responseStatus = 500;

        if (!isValidEndpoint()) {
            console.error('Invalid endpoint');
            return;
        }

        try {
            // Build the full URL with query parameters
            const url = new URL(endpoint); // Convert endpoint string into a URL object

            params.forEach(({ key, value }) => {
                if (key.trim() && value.trim()) {
                    url.searchParams.append(key, value);
                }
            });

            // Build the request options
            const options = {
                method,
                headers: headers.reduce((acc, { key, value }) => {
                    if (key.trim() && value.trim()) {
                        acc[key] = value;
                    }
                    return acc;
                }, {}),
            };

            if (['POST', 'PUT', 'PATCH'].includes(method) && body.trim()) {
                options.body = body;
                if (!options.headers['Content-Type']) {
                    options.headers['Content-Type'] = 'application/json';
                }
            }

            const startTime = performance.now();

            // Make the request
            const response = await fetch(url.toString(), options);

            // End timing and calculate response time
            const endTime = performance.now();
            const responseTime = Math.round(endTime - startTime);

            // Parse the response
            const responseBody = await response.json();
            const responseHeaders = Array.from(response.headers.entries()).map(([key, value]) => ({ key, value }));

            // Extract and parse cookies
            const setCookieHeader = response.headers.get('Set-Cookie');
            const cookies = setCookieHeader ? parseCookies(setCookieHeader) : [];
            responseStatus = response.status
            // Update the response state
            setResponse({
                headers: responseHeaders,
                body: JSON.stringify(responseBody, null, 2),
                cookies: cookies,
                respTime: responseTime || '',
                status: responseStatus || ''
            });
        } catch (error) {
            console.error('Error making request:', error);
            setResponse({
                headers: [],
                body: JSON.stringify({ error: 'Failed to fetch response' }, null, 2),
                cookies: [],
                status: 500
            });
        } finally {
            setUserApiInput({
                method: method,
                endpoint: endpoint,
                headers: headers,
                params: params,
                cookies: cookies,
                body: body,
                status: responseStatus,
            })
        }
    };

    return (
        <Drawer
            variant="persistent"
            open={drawerOpen}
            anchor='right'
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            className={Styles.apiDrawer}
        >
            {/* Enpoint and method selection tool bar */}
            <div className={Styles.drawerHeader}>
                <IconButton
                    onClick={onClose}
                    className={Styles.closeButton}
                    aria-label="close drawer"
                >
                    <ChevronRight />
                </IconButton>
                <Select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    size="small"
                    className={Styles.methodSelect}
                >
                    {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                </Select>
                <TextField
                    placeholder="Enter API endpoint"
                    value={endpoint}
                    onChange={handleEndpointChange}
                    size="small"
                    className={Styles.endpointInput}
                // error={endpoint !== '' && !isValidEndpoint()}
                // helperText={endpoint !== '' && !isValidEndpoint() ? 'Invalid endpoint' : ''}
                />
                <AivagamButton
                    buttonText={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5em' }}>Send <SendOutlined fontSize='small' /></span>}
                    classStyles={{ width: '100px', height: '40px', boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)', borderRadius: '6px', fontWeight: '600' }}
                    onClick={handleSendRequest}
                />


            </div>
            <div className={Styles.drawerContent}>
                {/* <div className={Styles.toolbar}>

                </div> */}

                {/* Request Section */}
                <Paper elevation={3} className={Styles.section}>
                    <Typography variant="h6" className={Styles.sectionTitle}>Request</Typography>
                    <Tabs
                        value={requestTab}
                        onChange={(_, v) => setRequestTab(v)}
                        className="section-tabs"
                    >
                        <Tab label="Headers" />
                        <Tab label="Params" />
                        <Tab label="Body" />
                        <Tab label="Cookies" />
                    </Tabs>
                    <div className={Styles.sectionContent}>
                        {requestTab === 0 && (
                            <div className={Styles.tabContent}>
                                <KeyValueEditor
                                    pairs={headers}
                                    onChange={handlePairChange(setHeaders)}
                                    onDelete={handlePairDelete(setHeaders)}
                                />
                                <Button
                                    className="add-button"
                                    onClick={handleAddPair(setHeaders)}
                                >
                                    <AddCircleOutline fontSize='small' sx={{ mr: '0.25em' }} />
                                    <span>Add Header</span>
                                </Button>
                            </div>
                        )}
                        {requestTab === 1 && (
                            <div className={Styles.tabContent}>
                                <KeyValueEditor
                                    pairs={params}
                                    onChange={handlePairChange(setParams)}
                                    onDelete={handlePairDelete(setParams)}
                                />
                                <Button
                                    className="add-button"
                                    onClick={handleAddPair(setParams)}
                                >
                                    <AddCircleOutline fontSize='small' sx={{ mr: '0.25em' }} />
                                    <span>Add Parameter</span>
                                </Button>
                            </div>
                        )}
                        {requestTab === 2 && (
                            <div className={Styles.tabContent}>
                                <TextField
                                    multiline
                                    rows={10}
                                    fullWidth
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Request body (JSON, XML, etc.)"
                                    className="body-field"
                                />
                            </div>
                        )}
                        {requestTab === 3 && (
                            <div className={Styles.tabContent}>
                                <KeyValueEditor
                                    pairs={cookies}
                                    onChange={handlePairChange(setCookies)}
                                    onDelete={handlePairDelete(setCookies)}
                                />
                                <Button
                                    className={Styles.addButton}
                                    onClick={handleAddPair(setCookies)}
                                >
                                    <AddCircleOutline fontSize='small' sx={{ mr: '0.25em' }} />
                                    <span>Add Cookie</span>
                                </Button>
                            </div>
                        )}
                    </div>
                </Paper>

                {/* Response Section */}
                <Paper elevation={3} className={Styles.section}>
                    <Typography variant="h6" className={Styles.sectionTitle}>Response</Typography>
                    <div className={Styles.responseInfo}>
                        <Typography variant="subtitle1">
                            Status: <span style={{ color: fetchResponseColor(response.status) }}>{response.status || '-'}</span>
                        </Typography>
                        <Typography variant="subtitle1">
                            Time: <span>{response.respTime ? `${response.respTime} ms` : '-'}</span>
                        </Typography>
                    </div>
                    <Tabs
                        value={responseTab}
                        onChange={(_, v) => setResponseTab(v)}
                        className="section-tabs"
                    >
                        <Tab label="Headers" />
                        <Tab label="Body" />
                        <Tab label="Cookies" />
                    </Tabs>
                    <div className={Styles.sectionContent}>
                        {responseTab === 0 && (
                            <div className={Styles.tabContent}>
                                <KeyValueEditor
                                    pairs={response.headers}
                                    onChange={() => { }}
                                    onDelete={() => { }}
                                />
                            </div>
                        )}
                        {responseTab === 1 && (
                            <div className={Styles.tabContent}>
                                <TextField
                                    multiline
                                    rows={10}
                                    fullWidth
                                    value={response.body}
                                    InputProps={{ readOnly: true }}
                                    className="body-field"
                                />
                            </div>
                        )}
                        {responseTab === 2 && (
                            <div className={Styles.tabContent}>
                                <KeyValueEditor
                                    pairs={response.cookies}
                                    onChange={() => { }}
                                    onDelete={() => { }}
                                />
                            </div>
                        )}
                    </div>
                </Paper>
            </div>
        </Drawer>
    );
};

export default APIDrawer;